import { Alert, Button, EmptyState, LoadingState, Modal, useToast } from '@orbind/ui'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { PLUGIN_SCOPE_LABEL, escopoEscreve, type PluginScope } from '@orbind/core'
import { useSession } from '../../lib/session.tsx'
import {
  nomeDoPlugin,
  useDesinstalarPlugin,
  useInstalarPlugin,
  usePluginsDoWorkspace,
  type PluginCadastrado,
} from '../plugin/usePlugins.ts'
import './plugins.css'

/**
 * Plugins do workspace (SPEC-20260926-2036) — a tela onde o administrador
 * INSTALA o que o super admin cadastrou no Console.
 *
 * O aceite de escopos é o coração desta tela, não um detalhe: é o único momento
 * em que alguém decide o que um código de terceiro vai poder fazer com os dados
 * da empresa. Por isso a lista aparece inteira, em português do Orbind (nunca
 * texto do plugin), e escrita fica visualmente separada de leitura.
 */

const TEXTO_DO_STATUS: Record<string, string> = {
  active: 'Instalado',
  pending: 'Instalação pendente — o plugin não confirmou',
  uninstalled: 'Desinstalado',
}

export function PluginsSection() {
  const navigate = useNavigate()
  const session = useSession()
  const consulta = usePluginsDoWorkspace()
  const instalar = useInstalarPlugin()
  const desinstalar = useDesinstalarPlugin()
  const toast = useToast()

  const [aInstalar, setAInstalar] = useState<PluginCadastrado | null>(null)

  const instalados = consulta.data?.instalados ?? []
  const idsInstalados = new Set(instalados.map((i) => i.pluginId))
  const disponiveis = (consulta.data?.disponiveis ?? []).filter((p) => !idsInstalados.has(p.id))

  const confirmarInstalacao = (plugin: PluginCadastrado) => {
    instalar.mutate(
      { id: plugin.id, scopes: plugin.scopes },
      {
        onSuccess: () => {
          setAInstalar(null)
          toast.push({ tone: 'success', message: `${nomeDoPlugin(plugin.name)} foi instalado neste workspace.` })
        },
        onError: (e) =>
          toast.push({
            tone: 'danger',
            message: e instanceof Error ? e.message : 'Não foi possível instalar o plugin.',
          }),
      },
    )
  }

  if (consulta.isPending) return <LoadingState variant="detail" label="Carregando os plugins" />

  if (consulta.isError) {
    return (
      <Alert
        tone="danger"
        title="Não foi possível carregar os plugins"
        action={
          <Button size="sm" icon="fas fa-redo" onClick={() => void consulta.refetch()}>
            Tentar de novo
          </Button>
        }
      >
        {consulta.error instanceof Error ? consulta.error.message : 'Tente novamente em instantes.'}
      </Alert>
    )
  }

  return (
    <div className="plg">
      <header className="plg__head">
        <h2 className="plg__title">Plugins</h2>
        <p className="plg__sub">
          Plugins são aplicativos de terceiros que estendem o Orbind. Eles rodam fora daqui, na infraestrutura de quem
          os publica, e só acessam o que você autorizar na instalação. Quem cadastra quais plugins podem existir nesta
          instalação é o Console.
        </p>
      </header>

      <section className="plg__bloco" aria-label="Instalados neste workspace">
        <h3 className="plg__h3">Instalados</h3>
        {instalados.length === 0 ? (
          <p className="plg__vazio">Nenhum plugin instalado neste workspace.</p>
        ) : (
          <ul className="plg__lista">
            {instalados.map((i) => (
              <li key={i.id} className="plg__card">
                <div className="plg__card-main">
                  <span className="plg__nome">{nomeDoPlugin(i.name)}</span>
                  <span className="plg__meta">
                    {i.vendor?.name ? `${i.vendor.name} · ` : ''}versão {i.version} · {i.key}
                  </span>
                  {/* status nunca só por cor: a palavra está escrita */}
                  <span className={`plg__status plg__status--${i.status}`}>
                    <i className={i.status === 'active' ? 'fas fa-check-circle' : 'fas fa-exclamation-circle'} aria-hidden />
                    {TEXTO_DO_STATUS[i.status] ?? i.status}
                  </span>
                  <span className="plg__escopos-resumo">
                    {i.acceptedScopes.length === 0
                      ? 'Sem permissões concedidas'
                      : `${i.acceptedScopes.length} ${i.acceptedScopes.length === 1 ? 'permissão concedida' : 'permissões concedidas'}`}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  icon="fas fa-trash"
                  disabled={desinstalar.isPending}
                  onClick={() =>
                    desinstalar.mutate(i.id, {
                      onSuccess: () =>
                        toast.push({ tone: 'success', message: `${nomeDoPlugin(i.name)} foi desinstalado.` }),
                      onError: (e) =>
                        toast.push({
                          tone: 'danger',
                          message: e instanceof Error ? e.message : 'Não foi possível desinstalar.',
                        }),
                    })
                  }
                >
                  Desinstalar
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="plg__bloco" aria-label="Disponíveis para instalar">
        <h3 className="plg__h3">Disponíveis</h3>
        {disponiveis.length === 0 ? (
          <EmptyState
            icon="fas fa-plug"
            title="Nenhum plugin disponível"
            description="Os plugins que aparecem aqui são cadastrados no Console pelo super admin da instalação."
            action={
              // quem PODE resolver vê o caminho, não só a explicação
              session.isSuperAdmin ? (
                <Button variant="primary" icon="fas fa-arrow-right" onClick={() => navigate('/console/plugins')}>
                  Cadastrar no Console
                </Button>
              ) : undefined
            }
          />
        ) : (
          <ul className="plg__lista">
            {disponiveis.map((p) => (
              <li key={p.id} className="plg__card">
                <div className="plg__card-main">
                  <span className="plg__nome">{nomeDoPlugin(p.name)}</span>
                  <span className="plg__meta">
                    {p.vendor?.name ? `${p.vendor.name} · ` : ''}versão {p.version} · {p.key}
                  </span>
                  <span className="plg__escopos-resumo">
                    {p.scopes.length === 0
                      ? 'Não pede acesso a nenhum dado'
                      : `Pede ${p.scopes.length} ${p.scopes.length === 1 ? 'permissão' : 'permissões'}`}
                  </span>
                </div>
                <Button variant="primary" icon="fas fa-download" onClick={() => setAInstalar(p)}>
                  Instalar
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* O aceite. Instalar é dar acesso aos dados da empresa a código que não
          é nosso — a lista inteira aparece antes, nunca depois. */}
      <Modal
        open={aInstalar !== null}
        onClose={() => setAInstalar(null)}
        title={aInstalar ? `Instalar ${nomeDoPlugin(aInstalar.name)}` : 'Instalar plugin'}
        width={520}
        footer={
          <>
            <Button variant="ghost" onClick={() => setAInstalar(null)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              icon="fas fa-check"
              disabled={instalar.isPending}
              onClick={() => aInstalar && confirmarInstalacao(aInstalar)}
            >
              {instalar.isPending ? 'Instalando…' : 'Aceitar e instalar'}
            </Button>
          </>
        }
      >
        {aInstalar === null ? null : (
          <div className="plg-aceite">
            <p className="plg-aceite__intro">
              <strong>{nomeDoPlugin(aInstalar.name)}</strong>
              {aInstalar.vendor?.name ? `, de ${aInstalar.vendor.name},` : ''} roda em{' '}
              <code className="plg-aceite__origem">{new URL(aInstalar.baseUrl).host}</code> — fora do Orbind. Ao
              instalar, você autoriza esse serviço a:
            </p>

            {aInstalar.scopes.length === 0 ? (
              <p className="plg-aceite__nada">
                Este plugin não pede acesso a nenhum dado. Ele só vai poder mostrar telas dentro do Orbind.
              </p>
            ) : (
              <ul className="plg-aceite__escopos">
                {[...aInstalar.scopes]
                  // escrita primeiro: é o que muda dado, e é o que precisa ser lido
                  .sort((a, b) => Number(escopoEscreve(b)) - Number(escopoEscreve(a)))
                  .map((s: PluginScope) => (
                    <li key={s} className={escopoEscreve(s) ? 'plg-aceite__escopo plg-aceite__escopo--escreve' : 'plg-aceite__escopo'}>
                      <i className={escopoEscreve(s) ? 'fas fa-pen' : 'fas fa-eye'} aria-hidden />
                      <span>
                        {PLUGIN_SCOPE_LABEL[s] ?? s}
                        {escopoEscreve(s) ? <em className="plg-aceite__tag">altera dados</em> : null}
                      </span>
                    </li>
                  ))}
              </ul>
            )}

            <p className="plg-aceite__nota">
              Você pode desinstalar a qualquer momento, e o acesso é cortado na hora.
            </p>
          </div>
        )}
      </Modal>
    </div>
  )
}
